import { Component, Inject, OnDestroy } from "@angular/core";
import { AbstractControl, FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import type {
  AttachPrintCardShipmentPayload,
  AttachShipmentDialogData,
  PrintCardOrder,
} from "../print-card-orders.interface";
import { PrintCardOrdersService } from "../print-card-orders.service";

const requiredValidator = (control: AbstractControl) =>
  Validators.required(control);

@Component({
  selector: "app-attach-shipment-dialog",
  templateUrl: "./attach-shipment-dialog.component.html",
  styleUrls: ["./attach-shipment-dialog.component.scss"],
  standalone: false,
})
export class AttachShipmentDialogComponent implements OnDestroy {
  readonly form = this.fb.nonNullable.group({
    carrier: this.fb.nonNullable.control<"UPS" | "OTHER">("UPS", {
      validators: [requiredValidator],
    }),
    trackingNumber: this.fb.nonNullable.control("", {
      validators: [requiredValidator, Validators.maxLength(80)],
    }),
    carrierName: this.fb.nonNullable.control(""),
    trackingUrl: this.fb.nonNullable.control(""),
  });
  saving = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: AttachShipmentDialogData,
    private readonly dialogRef: MatDialogRef<
      AttachShipmentDialogComponent,
      PrintCardOrder | undefined
    >,
    private readonly fb: FormBuilder,
    private readonly ordersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
  ) {
    this.form.controls.carrier.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((carrier) => {
        if (carrier === "OTHER") {
          this.form.controls.carrierName.setValidators([
            requiredValidator,
            Validators.maxLength(120),
          ]);
        } else {
          this.form.controls.carrierName.clearValidators();
          this.form.controls.carrierName.setValue("");
          this.form.controls.trackingUrl.setValue("");
        }
        this.form.controls.carrierName.updateValueAndValidity({
          emitEvent: false,
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) {
      return;
    }

    const value = this.form.getRawValue();
    const trackingNumber = value.trackingNumber.trim();
    if (!trackingNumber) {
      return;
    }
    if (value.carrier === "UPS" && trackingNumber.length > 40) {
      this.toastr.error("printCardOrders.attachShipment.failed");
      return;
    }

    const payload: AttachPrintCardShipmentPayload =
      value.carrier === "OTHER"
        ? {
            carrier: "OTHER",
            carrierName: value.carrierName.trim(),
            trackingNumber,
            ...(value.trackingUrl.trim()
              ? { trackingUrl: value.trackingUrl.trim() }
              : {}),
          }
        : {
            carrier: "UPS",
            trackingNumber,
          };

    this.saving = true;
    this.ordersService
      .attachShipment(this.data.orderId, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.toastr.success("printCardOrders.attachShipment.success");
          this.dialogRef.close(order);
        },
        error: (error) => {
          this.toastr.apiError(error, "printCardOrders.attachShipment.failed");
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
